"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import React from "react";

import BannerCta from "@/components/banners/BannerCta";
import PublicBannerCreative from "@/components/banners/PublicBannerCreative";
import RotatingBanner from "@/components/banners/RotatingBanner";
import OptimizedImage from "@/components/common/images/OptimizedImage";
import { usePublicBanner } from "@/hooks/banners/usePublicBanner";
import { Link } from "@/i18n/routing";
import { BANNER_SLOTS } from "@/lib/banners/constants";
import { cn } from "@/lib/utils";
import tradeInImageMobile from "@/public/assets/images/christmas-banner-home-mobile.webp";
import tradeInImageDesktopAr from "@/public/assets/images/trade-in-ar-horizontal.png";
import tradeInImageMobileAr from "@/public/assets/images/trade-in-ar-mobile.png";
import tradeInImageDesktop from "@/public/assets/images/trade-in-horizontal.png";
import {
  default as tradeInImageMobileAvif,
  default as tradeInImageMobileWebp,
} from "@/public/assets/images/trade-in-mobile.png";

const EscapeRoomOfferModal = dynamic(() => import("@/components/banners/EscapeRoomOfferModal"));

type ExchangeCarCardProps = {
  variant: "desktop" | "mobile";
  page?: "home" | "buy-page";
  /** Banner button opens the offer popup. Defaults to on for the home page. Pass false to follow the CTA link. */
  ctaOpensOffer?: boolean;
};

const ExchangeCarCard: React.FC<ExchangeCarCardProps> = ({ variant, page = "buy-page", ctaOpensOffer }) => {
  const locale = useLocale();
  const t = useTranslations("Common");
  const isArabic = locale === "ar";

  const isHomePage = page === "home";
  const openOfferOnCta = ctaOpensOffer ?? isHomePage;
  const [offerOpen, setOfferOpen] = React.useState(false);
  const openOffer = () => setOfferOpen(true);

  const exchangeBannerStrategy =
    variant === "desktop"
      ? { mode: "fixed" as const, fixedResolutionKey: "desktop" as const }
      : {
          mode: "responsive" as const,
          breakpoints: {
            mobileMaxWidth: 639,
            tabletMaxWidth: 1279,
          },
          map: {
            mobile: "mobile" as const,
            tablet: isHomePage ? ("tablet" as const) : ("desktop" as const),
            desktop: "desktop" as const,
          },
        };

  const { banners: exchangeBanners, resolutionKey } = usePublicBanner({
    slotKey: isHomePage ? BANNER_SLOTS.homePageBanner : BANNER_SLOTS.buyPageSecondBanner,
    locale,
    strategy: exchangeBannerStrategy,
  });

  const desktopBanners = variant === "desktop" ? exchangeBanners : [];
  const mobileBanners = variant === "mobile" && resolutionKey === "mobile" ? exchangeBanners : [];
  const tabletDesktopBanners = variant === "mobile" && resolutionKey !== "mobile" ? exchangeBanners : [];

  // Select images based on locale
  const desktopImage = isArabic ? tradeInImageDesktopAr : tradeInImageDesktop;
  const tradeInImageTab = isArabic ? tradeInImageDesktopAr : tradeInImageDesktop;
  const mobileImage = isArabic ? tradeInImageMobileAr : tradeInImageMobile;

  if (variant === "desktop") {
    return (
      <>
        <div className="relative my-6">
        {desktopBanners.length ? (
          <RotatingBanner banners={desktopBanners} transition="slide">
            {(banner, ctaBanner, meta) => (
              <PublicBannerCreative
                banner={banner}
                ctaBanner={ctaBanner}
                withKenBurns={meta?.withKenBurns}
                imageClassName="w-full rounded-lg xl:rounded-4xl"
                loading="lazy"
                isBanner
                alt={banner.bannerName || "trade-in"}
                ctaLabelFallback={t("checkYourEligibility")}
                ctaHrefFallback="/used-car-finance"
                ctaClassName="!py-2.5 !text-[8px] !font-semibold xl:px-5 xl:py-3 xl:!text-base 2xl:!text-lg rtl:left-5 rtl:right-auto"
                onCtaClick={openOfferOnCta ? openOffer : undefined}
              />
            )}
          </RotatingBanner>
        ) : (
          <>
            <Image
              src={desktopImage}
              width={1200}
              height={0}
              alt="trade-in"
              className="w-full rounded-lg xl:rounded-4xl"
              loading="lazy"
            />
            {openOfferOnCta ? (
              <button
                type="button"
                onClick={openOffer}
                className="absolute right-5 top-1/2 z-10 -translate-y-1/2 rounded-xl bg-white px-3 py-2 text-center font-urbanist text-[0.5rem] font-semibold text-black transition-all duration-300 hover:bg-white/90 hover:text-black xl:px-5 xl:py-3 xl:text-sm 2xl:text-base rtl:left-5 rtl:right-auto"
              >
                {t("checkYourEligibility")}
              </button>
            ) : (
              <Link href={"/used-car-finance"}>
                <button className="absolute right-5 top-1/2 z-10 -translate-y-1/2 rounded-xl bg-white px-3 py-2 text-center font-urbanist text-[0.5rem] font-semibold text-black transition-all duration-300 hover:bg-white/90 hover:text-black xl:px-5 xl:py-3 xl:text-sm 2xl:text-base rtl:left-5 rtl:right-auto">
                  {t("checkYourEligibility")}
                </button>
              </Link>
            )}
          </>
        )}
      </div>
      {openOfferOnCta ? <EscapeRoomOfferModal isOpen={offerOpen} onClose={() => setOfferOpen(false)} /> : null}
      </>
    );
  }

  if (variant === "mobile") {
    return (
      <>
        <div className="relative my-4 sm:hidden">
          {mobileBanners.length ? (
            <RotatingBanner banners={mobileBanners} transition="slide">
              {(banner, ctaBanner, meta) => (
                <>
                  <div className="relative h-[280px] w-full ixs:h-[320px] xs:h-[500px]">
                    <PublicBannerCreative
                      banner={banner}
                      withKenBurns={meta?.withKenBurns}
                      width={400}
                      height={0}
                      alt={banner.bannerName || "trade-in"}
                      imageClassName="rounded-4xl object-contain rtl:ps-2"
                      loading="lazy"
                      isBanner
                      showCta={false}
                    />
                  </div>
                  {ctaBanner ? (
                    <BannerCta
                      banner={ctaBanner}
                      labelFallback={t("checkYourEligibility")}
                      hrefFallback="/used-car-finance"
                      className="bottom-2 w-9/12 !rounded-lg !py-1.5 font-medium"
                      onClick={openOfferOnCta ? openOffer : undefined}
                    />
                  ) : null}
                </>
              )}
            </RotatingBanner>
          ) : (
            <>
              <div className="relative h-[280px] w-full ixs:h-[320px] xs:h-[500px]">
                <picture>
                  <source srcSet={isArabic ? tradeInImageMobileAr.src : tradeInImageMobileWebp.src} type="image/webp" />
                  {!isArabic && <source srcSet={tradeInImageMobileAvif.src} type="image/avif" />}
                  <OptimizedImage
                    src={mobileImage}
                    width={400}
                    height={0}
                    alt="trade-in"
                    className="rounded-4xl object-contain"
                    loading="lazy"
                  />
                </picture>
              </div>
              {openOfferOnCta ? (
                <button
                  type="button"
                  onClick={openOffer}
                  className="absolute bottom-2 left-1/2 z-10 w-9/12 -translate-x-1/2 rounded-lg bg-white px-4 py-1.5 text-center font-urbanist text-sm font-medium text-black hover:text-black"
                >
                  {t("checkYourEligibility")}
                </button>
              ) : (
                <Link href={"/used-car-finance"}>
                  <button className="absolute bottom-2 left-1/2 z-10 w-9/12 -translate-x-1/2 rounded-lg bg-white px-4 py-1.5 text-center font-urbanist text-sm font-medium text-black hover:text-black">
                    {t("checkYourEligibility")}
                  </button>
                </Link>
              )}
            </>
          )}
        </div>
        <div className="relative my-1 hidden sm:block xl:hidden">
          {tabletDesktopBanners.length ? (
            <RotatingBanner banners={tabletDesktopBanners} transition="slide">
              {(banner, ctaBanner, meta) => (
                <PublicBannerCreative
                  banner={banner}
                  ctaBanner={ctaBanner}
                  withKenBurns={meta?.withKenBurns}
                  alt={banner.bannerName || "trade-in"}
                  imageClassName="mt-4 w-full rounded-2xl"
                  loading="lazy"
                  isBanner
                  ctaLabelFallback={t("checkYourEligibility")}
                  ctaHrefFallback="/used-car-finance"
                  ctaClassName={cn(
                    isHomePage ? "!px-4 !py-2.5 !text-[14px] !font-semibold" : "!text-[12px] !font-semibold",
                  )}
                  onCtaClick={openOfferOnCta ? openOffer : undefined}
                />
              )}
            </RotatingBanner>
          ) : (
            <>
              <Image
                src={tradeInImageTab}
                width={800}
                height={0}
                alt="trade-in"
                className="mt-4 w-full rounded-2xl"
                loading="lazy"
              />
              {openOfferOnCta ? (
                <button
                  type="button"
                  onClick={openOffer}
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-lg bg-white p-2 text-center font-urbanist text-[0.6rem] text-black transition-all duration-300 hover:bg-white/90 hover:text-black md:text-xs rtl:left-2 rtl:right-auto rtl:md:left-4"
                >
                  {t("checkYourEligibility")}
                </button>
              ) : (
                <Link href={"/used-car-finance"}>
                  <button className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-lg bg-white p-2 text-center font-urbanist text-[0.6rem] text-black transition-all duration-300 hover:bg-white/90 hover:text-black md:text-xs rtl:left-2 rtl:right-auto rtl:md:left-4">
                    {t("checkYourEligibility")}
                  </button>
                </Link>
              )}
            </>
          )}
        </div>
        {openOfferOnCta ? <EscapeRoomOfferModal isOpen={offerOpen} onClose={() => setOfferOpen(false)} /> : null}
      </>
    );
  }

  return null;
};

export default ExchangeCarCard;
